// GraphQL Mutations
import { gql, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Palette } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import type { ServiceCategory } from "@/gql/graphql";

const CREATE_SERVICE_CATEGORY_MUTATION = gql`
  mutation CreateServiceCategory($salonId: ID!, $input: CreateServiceCategoryInput!) {
    createServiceCategory(salonId: $salonId, input: $input) {
      id
      name
      description
      color
      icon
      sortOrder
      isArchived
      allowOnlineBooking
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_SERVICE_CATEGORY_MUTATION = gql`
  mutation UpdateServiceCategory($salonId: ID!, $categoryId: ID!, $input: UpdateServiceCategoryInput!) {
    updateServiceCategory(salonId: $salonId, categoryId: $categoryId, input: $input) {
      id
      name
      description
      color
      icon
      sortOrder
      isArchived
      allowOnlineBooking
      createdAt
      updatedAt
    }
  }
`;

// Form validation schema - will be created with translated messages
const createCategoryFormSchema = (t: any) =>
  z.object({
    name: z
      .string()
      .min(1, t("categoryDialog.validation.categoryNameRequired"))
      .max(50, t("categoryDialog.validation.nameMaxLength")),
    description: z.string().optional(),
    color: z.string().regex(/^#[0-9A-F]{6}$/i, t("categoryDialog.validation.validHexColor")),
    icon: z.string().optional(),
    allowOnlineBooking: z.boolean(),
    isArchived: z.boolean().optional(),
  });

type CategoryFormData = z.infer<ReturnType<typeof createCategoryFormSchema>>;

interface CategoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category?: ServiceCategory | null;
  salonId: string;
  onSuccess?: () => void;
}

// Predefined color palette for categories
const COLOR_PALETTE = [
  "#3b82f6", // Blue
  "#10b981", // Emerald
  "#8b5cf6", // Violet
  "#f59e0b", // Amber
  "#ef4444", // Red
  "#06b6d4", // Cyan
  "#84cc16", // Lime
  "#f97316", // Orange
  "#ec4899", // Pink
  "#6366f1", // Indigo
  "#14b8a6", // Teal
  "#a855f7", // Purple
];

export function CategoryDialog({
  open,
  onOpenChange,
  category,
  salonId,
  onSuccess,
}: CategoryDialogProps) {
  const { t } = useTranslation("services");
  const [selectedColor, setSelectedColor] = useState("#3b82f6");
  const isEditing = Boolean(category);

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(createCategoryFormSchema(t)),
    defaultValues: {
      name: "",
      description: "",
      color: "#3b82f6",
      icon: "",
      allowOnlineBooking: true,
      isArchived: false,
    },
  });

  // Mutations
  const [createCategory, { loading: createLoading }] = useMutation(
    CREATE_SERVICE_CATEGORY_MUTATION,
    {
      refetchQueries: ["ServicesWithCategories"],
      onCompleted: () => {
        toast.success(t("categoryDialog.messages.categoryCreatedSuccess"));
        onOpenChange(false);
        form.reset();
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(t("categoryDialog.messages.createCategoryError", { error: error.message }));
      },
    }
  );

  const [updateCategory, { loading: updateLoading }] = useMutation(
    UPDATE_SERVICE_CATEGORY_MUTATION,
    {
      refetchQueries: ["ServicesWithCategories"],
      onCompleted: () => {
        toast.success(t("categoryDialog.messages.categoryUpdatedSuccess"));
        onOpenChange(false);
        onSuccess?.();
      },
      onError: (error) => {
        toast.error(t("categoryDialog.messages.updateCategoryError", { error: error.message }));
      },
    }
  );

  const loading = createLoading || updateLoading;

  // Reset form when dialog opens/closes or category changes
  useEffect(() => {
    if (open) {
      if (category) {
        form.reset({
          name: category.name,
          description: category.description || "",
          color: category.color,
          icon: category.icon || "",
          allowOnlineBooking: category.allowOnlineBooking,
          isArchived: category.isArchived,
        });
        setSelectedColor(category.color);
      } else {
        form.reset({
          name: "",
          description: "",
          color: "#3b82f6",
          icon: "",
          allowOnlineBooking: true,
          isArchived: false,
        });
        setSelectedColor("#3b82f6");
      }
    }
  }, [open, category, form]);

  const onSubmit = async (data: CategoryFormData) => {
    try {
      const input = {
        ...data,
        color: selectedColor,
        // For new categories, let backend automatically assign next sequential sortOrder
        // When editing, preserve existing sortOrder (don't include it in input)
      };

      if (isEditing && category) {
        await updateCategory({
          variables: {
            salonId,
            categoryId: category.id,
            input,
          },
        });
      } else {
        await createCategory({
          variables: {
            salonId,
            input,
          },
        });
      }
    } catch (_error) {
      // Error handled by mutation onError callbacks
    }
  };

  const handleColorSelect = (color: string) => {
    setSelectedColor(color);
    form.setValue("color", color, { shouldValidate: true });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t("categoryDialog.editTitle") : t("categoryDialog.createTitle")}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t("categoryDialog.editDescription")
              : t("categoryDialog.createDescription")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("categoryDialog.fields.categoryName")}</Label>
              <Input
                id="name"
                placeholder={t("categoryDialog.placeholders.categoryName")}
                {...form.register("name")}
              />
              {form.formState.errors.name && (
                <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t("categoryDialog.fields.description")}</Label>
              <Textarea
                id="description"
                placeholder={t("categoryDialog.placeholders.description")}
                className="resize-none"
                rows={2}
                {...form.register("description")}
              />
              <p className="text-sm text-muted-foreground">
                {t("categoryDialog.hints.description")}
              </p>
              {form.formState.errors.description && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.description.message}
                </p>
              )}
            </div>
          </div>

          {/* Color Selection */}
          <div className="space-y-3">
            <Label>{t("categoryDialog.fields.categoryColor")}</Label>
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg border-2 border-gray-200 flex items-center justify-center"
                style={{ backgroundColor: selectedColor }}
              >
                <Palette className="w-4 h-4 text-white" />
              </div>
              <Badge variant="outline">{selectedColor}</Badge>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {COLOR_PALETTE.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${
                    selectedColor === color ? "border-gray-900 shadow-md" : "border-gray-200"
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleColorSelect(color)}
                  title={color}
                />
              ))}
            </div>
            <div className="space-y-2">
              <Input
                type="text"
                placeholder={t("categoryDialog.placeholders.hexColor")}
                value={selectedColor}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedColor(value);
                  form.setValue("color", value);
                }}
                className="font-mono text-sm"
              />
              {form.formState.errors.color && (
                <p className="text-sm text-destructive">{form.formState.errors.color.message}</p>
              )}
            </div>
          </div>

          {/* Advanced Settings */}
          <div className="space-y-4">
            <div className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-base">{t("categoryDialog.fields.allowOnlineBooking")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t("categoryDialog.hints.allowOnlineBooking")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {form.watch("allowOnlineBooking") ? (
                  <Eye className="w-4 h-4 text-green-600" />
                ) : (
                  <EyeOff className="w-4 h-4 text-muted-foreground" />
                )}
                <Switch
                  checked={form.watch("allowOnlineBooking")}
                  onCheckedChange={(checked) => form.setValue("allowOnlineBooking", checked)}
                />
              </div>
            </div>

            {isEditing && (
              <div className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label className="text-base">{t("categoryDialog.fields.archiveCategory")}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t("categoryDialog.hints.archiveCategory")}
                  </p>
                </div>
                <Switch
                  checked={form.watch("isArchived")}
                  onCheckedChange={(checked) => form.setValue("isArchived", checked)}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {t("categoryDialog.actions.cancel")}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading
                ? t("categoryDialog.actions.saving")
                : isEditing
                  ? t("categoryDialog.actions.updateCategory")
                  : t("categoryDialog.actions.createCategory")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
